import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ChevronDown, RefreshCw, X, AlertCircle, Download, Trash2 } from 'lucide-react';
import { apiFetch } from '@/lib/api';

interface LogEntry {
  level: string;
  time: number;
  context?: string;
  message: string;
  userId?: number;
  requestId?: string;
  error?: string;
  stack?: string;
  [key: string]: any;
}

interface LogsResponse {
  logs: LogEntry[];
  stats: {
    total: number;
    levels: Record<string, number>;
  };
  filters: any;
}

export default function AdminLogsPage() {
  const [level, setLevel] = useState<string>('all');
  const [context, setContext] = useState<string>('');
  const [contains, setContains] = useState<string>('');
  const [userId, setUserId] = useState<string>('');
  const [requestId, setRequestId] = useState<string>('');
  const [limit, setLimit] = useState<number>(100);
  const [autoRefresh, setAutoRefresh] = useState<boolean>(false);

  const queryParams = new URLSearchParams();
  if (level && level !== 'all') queryParams.set('level', level);
  if (context) queryParams.set('context', context);
  if (contains) queryParams.set('contains', contains);
  if (userId) queryParams.set('userId', userId);
  if (requestId) queryParams.set('requestId', requestId);
  queryParams.set('limit', limit.toString());

  const { data, refetch, isLoading } = useQuery<LogsResponse>({
    queryKey: ['admin-logs', queryParams.toString()],
    queryFn: async () => {
      const res = await apiFetch(`/api/admin/logs/recent?${queryParams}`);
      if (!res.ok) throw new Error('Failed to fetch logs');
      return res.json();
    },
    refetchInterval: autoRefresh ? 5000 : false
  });

  const clearFilters = () => {
    setLevel('all');
    setContext('');
    setContains('');
    setUserId('');
    setRequestId('');
  };

  const downloadLogs = async () => {
    const params = new URLSearchParams();
    if (level && level !== 'all') params.set('level', level);
    if (context) params.set('context', context);
    if (contains) params.set('contains', contains);
    if (userId) params.set('userId', userId);
    if (requestId) params.set('requestId', requestId);

    const res = await apiFetch(`/api/admin/logs/download?${params}`);

    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `reactor-logs-${new Date().toISOString()}.json`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  const clearLogs = async () => {
    if (!confirm('Are you sure you want to clear all logs from the buffer?')) return;

    await apiFetch('/api/admin/logs/clear', {
      method: 'POST'
    });

    refetch();
  };

  const getLevelColor = (level: string) => {
    const normalizedLevel = (level || 'info').toString().toLowerCase();
    switch (normalizedLevel) {
      case 'error':
        return 'bg-red-100 text-red-800 border-red-300';
      case 'warn':
        return 'bg-amber-100 text-amber-800 border-amber-300';
      case 'info':
        return 'bg-blue-100 text-blue-800 border-blue-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const formatTime = (timestamp: number) => {
    return new Date(timestamp).toLocaleString();
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <AlertCircle className="h-6 w-6" />
                Emergency Bug Monitor
              </CardTitle>
              <CardDescription>
                Real-time server log viewer for Admin debugging
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button
                variant={autoRefresh ? 'default' : 'outline'}
                onClick={() => setAutoRefresh(!autoRefresh)}
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${autoRefresh ? 'animate-spin' : ''}`} />
                {autoRefresh ? 'Auto-refresh ON' : 'Auto-refresh OFF'}
              </Button>
              <Button onClick={() => refetch()} variant="outline">
                <RefreshCw className="h-4 w-4" />
              </Button>
              <Button onClick={downloadLogs} variant="outline">
                <Download className="h-4 w-4 mr-2" />
                Download
              </Button>
              <Button onClick={clearLogs} variant="outline">
                <Trash2 className="h-4 w-4 mr-2" />
                Clear
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Stats */}
          {data?.stats && (
            <div className="grid grid-cols-5 gap-4">
              <Card>
                <CardContent className="pt-4">
                  <div className="text-2xl font-bold">{data.stats.total}</div>
                  <p className="text-xs text-muted-foreground">Total Logs</p>
                </CardContent>
              </Card>
              {Object.entries(data.stats.levels).map(([level, count]) => (
                <Card key={level}>
                  <CardContent className="pt-4">
                    <div className="text-2xl font-bold">{count}</div>
                    <Badge className={getLevelColor(level)}>{level}</Badge>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Filters */}
          <div className="grid grid-cols-6 gap-4">
            <Select value={level} onValueChange={setLevel}>
              <SelectTrigger>
                <SelectValue placeholder="Level" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Levels</SelectItem>
                <SelectItem value="info">Info</SelectItem>
                <SelectItem value="warn">Warn</SelectItem>
                <SelectItem value="error">Error</SelectItem>
              </SelectContent>
            </Select>

            <Input
              placeholder="Context (e.g., CampaignService)"
              value={context}
              onChange={(e) => setContext(e.target.value)}
            />

            <Input
              placeholder="Contains text..."
              value={contains}
              onChange={(e) => setContains(e.target.value)}
            />

            <Input
              type="number"
              placeholder="User ID"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
            />

            <Input
              placeholder="Request ID"
              value={requestId}
              onChange={(e) => setRequestId(e.target.value)}
            />

            <Button
              variant="ghost"
              onClick={clearFilters}
              disabled={level === 'all' && !context && !contains && !userId && !requestId}
            >
              <X className="h-4 w-4 mr-2" />
              Clear
            </Button>
          </div>

          {/* Log Entries */}
          <div className="space-y-2 max-h-[600px] overflow-y-auto">
            {isLoading && <p className="text-center text-muted-foreground">Loading logs...</p>}

            {data?.logs.length === 0 && (
              <p className="text-center text-muted-foreground">No logs found matching filters</p>
            )}

            {data?.logs.map((log, idx) => (
              <Collapsible key={idx}>
                <Card className={`${log.level === 'error' ? 'border-red-300' : log.level === 'warn' ? 'border-amber-300' : ''}`}>
                  <CardContent className="pt-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 space-y-1">
                        <div className="flex items-center gap-2">
                          <Badge className={getLevelColor(log.level)}>
                            {(log.level || 'info').toString().toUpperCase()}
                          </Badge>
                          {log.context && (
                            <Badge variant="outline">{log.context}</Badge>
                          )}
                          <span className="text-xs text-muted-foreground">
                            {formatTime(log.time)}
                          </span>
                        </div>

                        <p className="font-medium">{log.message}</p>

                        {log.error && (
                          <p className="text-sm text-red-600">Error: {log.error}</p>
                        )}

                        <div className="flex gap-4 text-xs text-muted-foreground">
                          {log.userId && <span>User: {log.userId}</span>}
                          {log.requestId && <span>Request: {log.requestId.slice(0, 8)}...</span>}
                          {log.route && <span>Route: {log.route}</span>}
                          {log.method && <span>Method: {log.method}</span>}
                        </div>
                      </div>

                      {log.stack && (
                        <CollapsibleTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <ChevronDown className="h-4 w-4" />
                            Stack
                          </Button>
                        </CollapsibleTrigger>
                      )}
                    </div>

                    {log.stack && (
                      <CollapsibleContent>
                        <pre className="mt-4 p-4 bg-gray-100 rounded text-xs overflow-x-auto">
                          {log.stack}
                        </pre>
                      </CollapsibleContent>
                    )}
                  </CardContent>
                </Card>
              </Collapsible>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

